import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import { getGlobalSchema, getGlobalSeo } from '@tac/sanity-client/queries/global/seo';
import { getHomePage } from '@tac/sanity-client/queries/hsiyc/page';
import { type Dereference } from '@tac/sanity-client/type-utils';
import type { HsiycContentMatrix, Seo } from '@tac/sanity-client/types';
import { connect } from 'react-redux';

import type { Dispatch, RootState } from '@/app-state/store';
import ContentMatrix from '@/components/content-matrix';
import DiscoveryQuestionsInput from '@/components/discovery-questions-input';
import Layout from '@/components/layout';
import { CACHE } from '@/constants';
import { cn } from '@/lib/utils';
import { urlFor } from '@/utils/sanity-image';
import { useMaintenanceBanner } from '@/context/maintenance-banner';
import { getGlobalStructuredData } from '@/utils/seo/structured-data';
import { getAllMakesModels } from '@tac/vehicle-api-client/queries';
import type { GetAllMakesModelsQuery } from '@tac/vehicle-api-client/types';
import SearchV2 from '@/components/search-v2';
import { withAppProps, withAppProviders } from '@/utils/withAppProps';

type HomePageProps = {
  backgroundImage: SanityImageSource | null;
  secondaryBackgroundImage: SanityImageSource | null;
  contentMatrix: Dereference<HsiycContentMatrix>;
  filterActions: ReturnType<typeof mapDispatch>['filterActions'];
  filterState: ReturnType<typeof mapState>['filterState'];
  seo?: Seo;
  makesModels?: GetAllMakesModelsQuery['getAllMakesModels'];
};

const Home = ({ backgroundImage, secondaryBackgroundImage, contentMatrix, seo, makesModels }: HomePageProps) => {
  const maintenanceBanner = useMaintenanceBanner();
  const backgroundImageUrl = urlFor(backgroundImage)?.url();
  const secondaryBackgroundImageUrl = urlFor(secondaryBackgroundImage)?.url();

  return (
    <div className="relative min-h-screen">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed z-0"
        style={{ backgroundImage: secondaryBackgroundImageUrl ? `url(${secondaryBackgroundImageUrl})` : undefined }}
        aria-hidden
      />
      <div
        className="absolute inset-0 hidden bg-cover bg-center bg-no-repeat bg-fixed z-0 lg:block"
        style={{ backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined }}
        aria-hidden
      />
      <Layout className="relative z-10 min-h-screen bg-transparent" seo={seo}>
        <div className={cn('mt-8 isolate', maintenanceBanner?.enabled ? 'lg:mt-4' : 'lg:mt-24')}>
          <div className="container-default flex flex-col items-center gap-10 mb-20 lg:mb-30">
            <SearchV2 title="Find out how safe your car is" makesModels={makesModels} />
            <DiscoveryQuestionsInput />
          </div>
        </div>
        <div className="bg-greyscale-25">
          <ContentMatrix blocks={contentMatrix} />
        </div>
      </Layout>
    </div>
  );
};

const getStaticProps = withAppProps(async () => {
  const homePage = await getHomePage();
  const globalSeo = await getGlobalSeo('hsiyc');
  const globalSchema = await getGlobalSchema('hsiyc');
  const structuredData = getGlobalStructuredData(globalSeo, globalSchema);
  const seo = Object.assign({}, globalSeo?.seo, homePage?.seo, { structuredData });

  const { getAllMakesModels: makesModels } = await getAllMakesModels();

  return {
    props: {
      trackingMeta: {
        content_type: 'home',
        content_group: 'homepage',
      },
      backgroundImage: homePage?.backgroundImage ?? null,
      secondaryBackgroundImage: homePage?.secondaryBackgroundImage ?? null,
      contentMatrix: homePage?.contentMatrix ?? [],
      homePage,
      seo,
      makesModels,
    },
    revalidate: CACHE.REVALIDATE,
  };
});

const mapState = (state: RootState) => ({
  filterState: {
    selectedPriceRange: state.filters.selectedPriceRange,
    uiSelectedPriceRange: state.filters.uiSelectedPriceRange,
    selectedBodyStyles: state.filters.selectedBodyStyles,
    selectedLifestyles: state.filters.selectedLifestyles,
    selectedPowerTypes: state.filters.selectedPowerTypes,
  },
});

const mapDispatch = (dispatch: Dispatch) => ({
  filterActions: {
    addLifestyle: dispatch.filters.addLifestyle,
    removeLifestyle: dispatch.filters.removeLifestyle,
    updateSelectedBodyStyles: dispatch.filters.updateSelectedBodyStyles,
    updateSelectedPriceRange: dispatch.filters.updateSelectedPriceRange,
    updateUiSelectedPriceRange: dispatch.filters.updateUiSelectedPriceRange,
    addPowerType: dispatch.filters.addPowerType,
    removePowerType: dispatch.filters.removePowerType,
    updatePowerTypes: dispatch.filters.updatePowerTypes,
  },
});

export default withAppProviders(connect(mapState, mapDispatch)(Home));
export { getStaticProps };
